/**
 * Dialog de configuração do node Meta Pixel
 * Permite selecionar tipo de evento e parâmetros
 * 
 * RELEASE: 1.1.0 - 2026-03-08 - Adicionado botão de ajuda contextual (NodeHelpDialog)
 * RELEASE: 1.0.0 - 2026-03-08 - Novo dialog de rastreamento Meta Pixel
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Target, HelpCircle } from 'lucide-react';
import { FlowNode, MetaPixelEventType } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '@/components/tenant/flows/NodeIdField';
import { NodeHelpDialog } from './NodeHelpDialog';

/** Tipos de evento padrão do Meta Pixel */
const EVENT_TYPES: { value: MetaPixelEventType; label: string }[] = [
  { value: 'PageView', label: 'PageView' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'CompleteRegistration', label: 'CompleteRegistration' },
  { value: 'AddToCart', label: 'AddToCart' },
  { value: 'Custom', label: 'Evento Customizado' },
];

interface ConfigureMetaPixelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, any>) => void;
}

/**
 * Dialog para configurar evento do Meta Pixel (Facebook)
 */
export function ConfigureMetaPixelDialog({ open, onOpenChange, node, onSave }: ConfigureMetaPixelDialogProps) {
  const config = node.data.config || {};
  const [eventType, setEventType] = useState<MetaPixelEventType>(config.event_type || 'Lead');
  const [customEventName, setCustomEventName] = useState<string>(config.custom_event_name || '');
  const [params, setParams] = useState<Array<{ key: string; value: string }>>(
    Object.entries((config.params as Record<string, string>) || {}).map(([key, value]) => ({ key, value }))
  );
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  /** Controle do modal de ajuda */
  const [showHelp, setShowHelp] = useState(false);

  /** Adiciona par chave/valor */
  const addParam = () => {
    setParams(prev => [...prev, { key: '', value: '' }]);
  };

  /** Remove par pelo índice */
  const removeParam = (index: number) => {
    setParams(prev => prev.filter((_, i) => i !== index));
  };

  /** Atualiza chave ou valor */
  const updateParam = (index: number, field: 'key' | 'value', val: string) => {
    setParams(prev => prev.map((v, i) => i === index ? { ...v, [field]: val } : v));
  };

  /** Salva configuração */
  const handleSave = () => {
    const paramsRecord: Record<string, string> = {};
    params.forEach(p => {
      if (p.key.trim()) paramsRecord[p.key.trim()] = p.value;
    });

    onSave(node.id, {
      ...config,
      event_type: eventType,
      custom_event_name: eventType === 'Custom' ? customEventName : undefined,
      params: paramsRecord,
      nickname: nicknameRef.current?.getNickname(),
    });
    onOpenChange(false);
  };

  /** Valida se pode salvar */
  const canSave = eventType !== 'Custom' || customEventName.trim().length > 0;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-indigo-600" />
              Meta Pixel
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node + Apelido */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node.id}
            defaultNickname={config.nickname}
          />

          {/* Tipo de Evento */}
          <div className="space-y-2">
            <Label>Tipo de Evento *</Label>
            <Select value={eventType} onValueChange={(v) => setEventType(v as MetaPixelEventType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map(et => (
                  <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nome customizado (se Custom) */}
          {eventType === 'Custom' && (
            <div className="space-y-2">
              <Label>Nome do Evento Customizado *</Label>
              <Input
                value={customEventName}
                onChange={(e) => setCustomEventName(e.target.value)}
                placeholder="meu_evento_custom"
              />
            </div>
          )}

          {/* Parâmetros */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Parâmetros (opcional)</Label>
              <Button variant="outline" size="sm" onClick={addParam}>
                <Plus className="h-3 w-3 mr-1" /> Adicionar
              </Button>
            </div>

            {params.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={p.key}
                  onChange={(e) => updateParam(i, 'key', e.target.value)}
                  placeholder="currency, value..."
                  className="flex-1 h-8 text-sm"
                />
                <Input
                  value={p.value}
                  onChange={(e) => updateParam(i, 'value', e.target.value)}
                  placeholder="BRL, {{valor}}"
                  className="flex-1 h-8 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => removeParam(i)} className="h-8 px-2">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}

            {params.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                Nenhum parâmetro adicionado
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!canSave}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="tracking_meta_pixel" />
    </>
  );
}